import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Check, X } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Skeleton,
  MultiSelect,
  type MultiSelectOption,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { DetailHeader } from '@/components/detail-header';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import {
  useSurveyRecord,
  useUpdateSurveyRecord,
  type SurveyRecordScores,
} from '@/api/surveys';

/* ── Constants ───────────────────────────────────────────────────────────── */

const SCORE_FIELDS: { key: keyof SurveyRecordScores; label: string }[] = [
  { key: 'score_service_quality', label: 'Service Quality' },
  { key: 'score_customer_care', label: 'Customer Care' },
  { key: 'score_tech_support', label: 'Tech Support' },
  { key: 'score_issue_resolution', label: 'Issue Resolution' },
  { key: 'score_referral_willingness', label: 'Referral Willingness' },
];

const NOTE_FIELDS: { key: keyof SurveyRecordScores; label: string }[] = [
  { key: 'note_service_quality', label: 'Service Quality note' },
  { key: 'note_customer_care', label: 'Customer Care note' },
  { key: 'note_tech_support', label: 'Tech Support note' },
  { key: 'note_issue_resolution', label: 'Issue Resolution note' },
  { key: 'note_referral_willingness', label: 'Referral Willingness note' },
  { key: 'note_general', label: 'General note' },
  { key: 'improvement', label: 'Improvement suggestions' },
];

const UPSALE_OPTIONS: MultiSelectOption[] = [
  { value: 'CLOUD', label: 'Cloud' },
  { value: 'CDN', label: 'CDN' },
  { value: 'VNIS', label: 'VNIS' },
];

/** Format a nullable 1-10 score for display. */
function fmtScore(v: number | null | undefined): string {
  return v != null ? `${v} / 10` : '—';
}

/** Format ISO timestamp (no Z suffix from API) to local date+time. */
function fmtDateTime(iso: string): string {
  const d = new Date(iso + 'Z');
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── Score picker (inline edit) ─────────────────────────────────────────── */

function ScorePicker({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexWrap: 'wrap' }}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--vn-radius-sm)',
            border: `1px solid ${value === n ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
            background: value === n ? 'var(--vn-primary-subtle)' : 'transparent',
            color: value === n ? 'var(--vn-primary-text)' : 'var(--vn-fg-default)',
            fontWeight: value === n ? 'var(--vn-font-weight-semibold)' : 'var(--vn-font-weight-regular)',
            fontSize: 'var(--vn-font-size-sm)',
            cursor: 'pointer',
          }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */

/**
 * `/surveys/records/:recordId` — survey record detail with inline edit.
 * Read view: InfoGrid of scores + notes. Edit mode: score pickers + textareas.
 * Save calls `PATCH /surveys/:id/responses` (USER-triggered).
 */
export default function SurveyRecordDetailPage() {
  const { recordId } = useParams<{ recordId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const id = Number(recordId);
  const { data, isLoading, isError, refetch } = useSurveyRecord(id);
  const update = useUpdateSurveyRecord(id);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SurveyRecordScores>({});
  const [saveError, setSaveError] = useState('');

  const record = data?.result;

  const startEdit = () => {
    if (!record) return;
    setDraft({
      score_service_quality: record.score_service_quality ?? undefined,
      score_customer_care: record.score_customer_care ?? undefined,
      score_tech_support: record.score_tech_support ?? undefined,
      score_issue_resolution: record.score_issue_resolution ?? undefined,
      score_referral_willingness: record.score_referral_willingness ?? undefined,
      note_general: record.note_general ?? '',
      note_service_quality: record.note_service_quality ?? '',
      note_customer_care: record.note_customer_care ?? '',
      note_tech_support: record.note_tech_support ?? '',
      note_issue_resolution: record.note_issue_resolution ?? '',
      note_referral_willingness: record.note_referral_willingness ?? '',
      improvement: record.improvement ?? '',
      upsale_potential: record.upsale_potential ?? [],
    });
    setSaveError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({});
    setSaveError('');
  };

  const handleSave = async () => {
    setSaveError('');
    try {
      await update.mutateAsync(draft);
      toast.success('Survey record updated');
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to save.';
      setSaveError(msg);
      toast.error('Failed to save', { description: msg });
    }
  };

  const setField = (key: keyof SurveyRecordScores, value: number | string | string[]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  /* ── Loading / error states ────────────────────────────────────────────── */

  if (isLoading) {
    return (
      <Container size="full">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Skeleton style={{ height: 64 }} />
          <Skeleton style={{ height: 200 }} />
          <Skeleton style={{ height: 200 }} />
        </div>
      </Container>
    );
  }

  if (isError || !record) {
    return (
      <Container size="full">
        <Alert
          tone="danger"
          title="Failed to load survey record"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  /* ── Render ──────────────────────────────────────────────────────────────── */

  const scoreInfoItems = SCORE_FIELDS.map(({ key, label }) => ({
    label,
    value: fmtScore(record[key as keyof typeof record] as number | null),
  }));

  const noteInfoItems = NOTE_FIELDS.map(({ key, label }) => ({
    label,
    value: (record[key as keyof typeof record] as string | null) || '—',
    full: true as const,
  }));

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DetailHeader
          parent={{ label: 'Surveys', to: '/surveys' }}
          current={record.customer_name}
          idPrefix={`#${record.survey_record_id}`}
          title={record.customer_name}
          actions={
            editing ? (
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                <Button
                  variant="ghost"
                  startIcon={<X size={15} />}
                  onClick={cancelEdit}
                  disabled={update.isPending}
                >
                  Cancel
                </Button>
                <Button
                  startIcon={<Check size={15} />}
                  onClick={handleSave}
                  loading={update.isPending}
                  disabled={update.isPending}
                >
                  Save
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                startIcon={<Pencil size={15} />}
                onClick={startEdit}
              >
                Edit responses
              </Button>
            )
          }
        />

        {/* ── Meta card ───────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Record details</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid
              items={[
                { label: 'Customer', value: record.customer_name },
                { label: 'Surveyor', value: record.employee_name },
                { label: 'Service', value: record.service_name || '—' },
                { label: 'Average score', value: fmtScore(record.average_score) },
                { label: 'Created', value: fmtDateTime(record.created_at) },
                { label: 'Updated', value: fmtDateTime(record.updated_at) },
                {
                  label: 'Upsale potential',
                  value: record.upsale_potential?.length
                    ? record.upsale_potential.join(', ')
                    : '—',
                },
              ]}
            />
          </CardBody>
        </Card>

        {/* ── Scores card ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Satisfaction scores</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {editing ? (
              <>
                {SCORE_FIELDS.map(({ key, label }) => (
                  <Field key={key} label={`${label} (1–10)`}>
                    {() => (
                      <ScorePicker
                        value={draft[key] as number | undefined}
                        onChange={(v) => setField(key, v)}
                      />
                    )}
                  </Field>
                ))}
              </>
            ) : (
              <InfoGrid items={scoreInfoItems} />
            )}
          </CardBody>
        </Card>

        {/* ── Notes card ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Notes &amp; improvement</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {editing ? (
              <>
                {NOTE_FIELDS.map(({ key, label }) => (
                  <Field key={key} label={label}>
                    {({ id }) => (
                      <textarea
                        id={id}
                        value={(draft[key] as string) || ''}
                        onChange={(e) => setField(key, e.target.value)}
                        rows={2}
                        style={{
                          width: '100%',
                          padding: 'var(--vn-space-2) var(--vn-space-3)',
                          border: '1px solid var(--vn-border-default)',
                          borderRadius: 'var(--vn-radius-sm)',
                          fontSize: 'var(--vn-font-size-sm)',
                          color: 'var(--vn-fg-default)',
                          background: 'var(--vn-bg-surface)',
                          resize: 'vertical',
                          fontFamily: 'var(--vn-font-sans)',
                        }}
                      />
                    )}
                  </Field>
                ))}

                <Field label="Upsale potential">
                  {() => (
                    <MultiSelect
                      options={UPSALE_OPTIONS}
                      value={(draft.upsale_potential as string[]) ?? []}
                      onChange={(v) => setField('upsale_potential', v)}
                      placeholder="Select products with upsale potential…"
                    />
                  )}
                </Field>

                {saveError ? <Alert tone="danger" title={saveError} /> : null}
              </>
            ) : (
              <>
                <InfoGrid items={noteInfoItems} />
                <InfoGrid
                  items={[
                    {
                      label: 'Upsale potential',
                      value: record.upsale_potential?.length
                        ? record.upsale_potential.join(', ')
                        : '—',
                      full: true,
                    },
                  ]}
                />
              </>
            )}
          </CardBody>
        </Card>

        <div>
          <Button variant="ghost" onClick={() => navigate('/surveys')}>
            Back to surveys
          </Button>
        </div>
      </div>
    </Container>
  );
}
